import { ApplicationConfig, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';

import { provideRouter, NoPreloading, withPreloading, withNavigationErrorHandler } from '@angular/router';

import { routes } from './routes.service';
import { provideClientHydration, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { errorInterceptor } from './shared/interceptors/error.interceptor';
import { caixaContextInterceptor } from './shared/services/caixa-context.interceptor';
import { LOCALE_ID } from '@angular/core';
import localePt from '@angular/common/locales/pt';
import { registerLocaleData } from '@angular/common';
registerLocaleData(localePt)

/**
 * Recuperação de "stale chunk" após deploy: o build de produção usa hashes nos nomes dos chunks
 * (outputHashing: "all"), então sessões já abertas continuam referenciando os nomes antigos e
 * tomam 404 ao navegar para rotas lazy (tela branca). Ao detectar a falha de carregamento do
 * chunk, recarregamos a página UMA vez (guarda de 10s evita loop) — a sessão pega o index.html
 * novo + os chunks novos e se recupera sozinha. SSR-safe (só roda no browser).
 */
function reloadOnChunkLoadError(error: unknown): void {
  const message = (error as { message?: string })?.message ?? String(error);
  const isChunkError = /ChunkLoadError|Loading chunk|dynamically imported module/i.test(message);
  if (!isChunkError || typeof window === 'undefined') return;

  const RELOAD_KEY = 'chunkReload';
  const NUKE_KEY = 'chunkNuked';
  const WINDOW_MS = 60000;  // janela de observação das tentativas
  const MAX_ATTEMPTS = 2;   // reloads simples antes do last resort
  const now = Date.now();

  // 1) Já fizemos a limpeza total (last resort) há pouco e o erro PERSISTE → desiste de vez.
  //    Sem isto, limpar o storage zera o contador e cairíamos em loop infinito de limpar+recarregar.
  const nukedAt = Number(sessionStorage.getItem(NUKE_KEY) ?? 0);
  if (nukedAt && now - nukedAt < WINDOW_MS) return;

  // 2) Conta as tentativas de reload dentro da janela.
  let attempts = 0;
  try {
    const saved = JSON.parse(sessionStorage.getItem(RELOAD_KEY) ?? 'null');
    if (saved && now - saved.at < WINDOW_MS) attempts = saved.count ?? 0;
  } catch { /* storage indisponível/corrompido → trata como 0 tentativas */ }

  // 3) Até MAX_ATTEMPTS: só recarrega (pega index.html novo + chunks novos).
  if (attempts < MAX_ATTEMPTS) {
    try { sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ count: attempts + 1, at: now })); } catch { /* ignore */ }
    window.location.reload();
    return;
  }

  // 4) LAST RESORT: erros consecutivos após os reloads → o estado local pode estar corrompido.
  //    Apaga TODO o localStorage + sessionStorage, marca p/ não repetir, e recarrega uma última vez.
  try { localStorage.clear(); } catch { /* ignore */ }
  try { sessionStorage.clear(); } catch { /* ignore */ }
  try { sessionStorage.setItem(NUKE_KEY, String(now)); } catch { /* ignore */ }
  window.location.reload();
}

export const appConfig: ApplicationConfig = {
  providers: [provideZoneChangeDetection(), provideRouter(routes, withPreloading(NoPreloading), withNavigationErrorHandler(reloadOnChunkLoadError)), provideClientHydration(withHttpTransferCacheOptions({ includePostRequests: false })), provideAnimationsAsync(),

  provideServiceWorker('ngsw-worker.js', {
    enabled: !isDevMode(),
    registrationStrategy: 'registerWhenStable:30000'
  }),
  provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor, caixaContextInterceptor])),
  { provide: LOCALE_ID, useValue: 'pt-BR' },
  {
    provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
    useValue: {
      showDelay: 500,
      hideDelay: 0,
      touchendHideDelay: 0,
    } as MatTooltipDefaultOptions
  }]

};
